// Deleting the signed-in teacher's account, from this browser (#432).
//
// The server does the deleting — `deleteAccount` in functions/index.js, whose
// order is functions/deletion/run.js — and this is the client's half: prove
// the teacher is here, ask, and then forget the studio on this device.
//
// ── Why re-authenticate first ───────────────────────────────────────────────
//
// The callable refuses a sign-in older than five minutes
// (core/src/account_deletion.rs `sign_in_is_recent`), because a session that
// may be weeks old is not consent to destroy a studio. So the password is
// checked with Firebase immediately before the call, and the ID token is
// refreshed so the one the callable sees carries the new `auth_time`.
//
// An account with Google and no password (#567) has no password to check, so
// it signs in with Google again instead, in a pop-up, which sets the same
// `auth_time`. Which of the two is `reauthMethod` in domain/signIn.js. A
// pop-up and not a redirect: a redirect would leave the page in the middle of
// a deletion, and the dialog, the typed word and the refusal it may have to
// show would all be gone when the teacher came back.
//
// ── Why this device forgets the studio outright ─────────────────────────────
//
// A sign-out parks this teacher's owed writes for their return and keeps the
// outstanding-write notes (AuthContext `endSession`). There is no return from
// a deletion, so what that argument protects does not exist: whatever is left
// in the queue for this uid would sit there for ever, and the SDK's cache
// would hold the studio's documents until somebody else signed in. The button
// could not be pressed while writes were owed (core `deletion_blocker`), so
// what is discarded here is only dead letters and notes — never work the
// teacher was still waiting on.

import {
  EmailAuthProvider, reauthenticateWithCredential, reauthenticateWithPopup, signOut,
} from 'firebase/auth';
import { clearIndexedDbPersistence, terminate } from 'firebase/firestore';
import { auth, db, functions } from '../lib/firebase';
import { googleProvider } from '../lib/googleAuth';
import { ANALYTICS, trackEvent } from '../lib/analytics';
import { DELETION_REFUSAL, googleReauthRefusal } from '../domain/accountDeletion';
import { writeQueue } from './writes';
import { theReadPath } from './readStore';
import { clearWrite, outstandingWrites, studioOfWrite } from './outstandingWrites';

/** Where this browser records whose documents the SDK's cache holds (AuthContext). */
const SDK_CACHE_OWNER_KEY = 'coda-sdk-cache-owner';

/** How long to wait for the SDK's cache to go before leaving it to the next sign-in's sweep. */
const CACHE_BUDGET_MS = 3000;

/** A refusal the screen can say: `reason` is one of DELETION_REFUSAL. */
export class DeletionRefused extends Error {
  constructor(reason) {
    super(reason);
    this.name = 'DeletionRefused';
    this.reason = reason;
  }
}

// What a failed re-authentication means to the teacher.
//
// `invalid-credential` is what Firebase answers for a wrong password once
// email-enumeration protection is on (and `invalid-login-credentials` on some
// SDK versions); `wrong-password` without it. `user-mismatch` is a password
// that belongs to a different account — from the teacher's side, the wrong one.
export function reauthRefusal(err) {
  const code = String(err?.code || '');
  if (/wrong-password|invalid-credential|invalid-login-credentials|user-mismatch|missing-password/.test(code)) {
    return DELETION_REFUSAL.wrongPassword;
  }
  if (/network-request-failed/.test(code)) return DELETION_REFUSAL.offline;
  return DELETION_REFUSAL.failed;
}

// Whether the account is gone, when the call's own answer could not be read.
//
// A dropped connection after the server finished is the one case where "it
// failed" would be a lie in the harmful direction: the teacher would try
// again, and see an error, for an account that no longer exists. So the Auth
// user is asked directly; `user-not-found` and `user-token-expired` are
// Firebase's two ways of saying it has been deleted.
async function accountIsGone(user) {
  try {
    await user.reload();
    return false;
  } catch (err) {
    return /user-not-found|user-token-expired|user-disabled/.test(String(err?.code || ''));
  }
}

/**
 * Delete the signed-in teacher's account and studio.
 *
 * Resolves once the server has deleted the account and this device has
 * forgotten the studio; the caller then reloads the page (the Firestore client
 * has been terminated). Rejects with a `DeletionRefused` whose `reason` has a
 * sentence in the core — and in every rejection, the account still exists.
 *
 * `portalLogin` is the Family Portal's "Delete my login" (#774): the same
 * callable, told so, which then deletes a family's portal-only login and
 * refuses rather than delete a studio (functions/deletion/run.js). It is not
 * counted as a teacher's account deleted.
 */
export async function deleteOwnAccount({
  password, confirmation, method = 'password', portalLogin = false,
}) {
  const user = auth?.currentUser;
  if (!user) throw new DeletionRefused(DELETION_REFUSAL.failed);

  // The password, or Google, is checked before anything else can refuse, so
  // a wrong one is always said as itself rather than as a generic failure.
  if (method === 'google') {
    try {
      await reauthenticateWithPopup(user, googleProvider());
    } catch (err) {
      throw new DeletionRefused(googleReauthRefusal(err));
    }
  } else {
    try {
      await reauthenticateWithCredential(user, EmailAuthProvider.credential(user.email, password));
    } catch (err) {
      throw new DeletionRefused(reauthRefusal(err));
    }
  }
  if (!functions) throw new DeletionRefused(DELETION_REFUSAL.failed);
  // The token the callable sees must carry the new `auth_time`. A failure here
  // is not fatal: the callable will say `sign_in_too_old` if it mattered.
  await user.getIdToken(true).then(null, () => null);

  try {
    // Imported here rather than at the top: this module is reached from
    // AuthContext, which every screen and a good many jsdom tests load, and the
    // Functions SDK wants `fetch` the moment it is evaluated. Only a deletion
    // needs it.
    const { httpsCallable } = await import('firebase/functions');
    await httpsCallable(functions, 'deleteAccount')(portalLogin ? { confirmation, portalLogin: true } : { confirmation });
  } catch (err) {
    const reason = err?.details?.reason;
    if (reason) throw new DeletionRefused(reason);
    if (!(await accountIsGone(user))) {
      throw new DeletionRefused(/unavailable|network|deadline/.test(String(err?.code || ''))
        ? DELETION_REFUSAL.offline
        : DELETION_REFUSAL.failed);
    }
  }

  // Counted once the account is gone, and before this device forgets it: the
  // caller reloads the page only after that has finished, which leaves the
  // event time to be sent (#458). Reasoned from the order of the awaits, not
  // observed against a live stream.
  if (!portalLogin) trackEvent(ANALYTICS.accountDeleted);
  await forgetStudioOnThisDevice(user.uid);
}

/**
 * Everything this browser holds for `uid`, removed, and the session ended.
 *
 * Each step is attempted whatever the others do, and none of them can undo the
 * deletion, which has already happened on the server.
 */
export async function forgetStudioOnThisDevice(uid) {
  const steps = await Promise.allSettled([
    (async () => {
      const queue = writeQueue();
      for (const record of await queue.records()) {
        const paths = (record?.ops || []).map((op) => String(op?.path || ''));
        if (paths.length && paths.every((p) => p.split('/')[1] === uid)) await queue.discard(record.batchId);
      }
    })(),
    (async () => {
      for (const note of outstandingWrites()) {
        if (studioOfWrite(note) === uid) clearWrite(note.id);
      }
    })(),
    theReadPath().store.clearStudio(uid),
  ]);
  const failed = steps.filter((s) => s.status === 'rejected');
  if (failed.length) {
    console.error(`[auth] the account \`${uid}\` was deleted, but ${failed.length} of 3 clean-ups on this device did not finish:`,
      failed.map((f) => f.reason));
  }

  await signOut(auth).then(null, () => null);

  // The SDK's own cache: terminated first, because it can only be cleared from
  // a client that is not running. Raced against a budget for the reason
  // AuthContext's sweep gives — another tab holding the database open blocks
  // the delete for as long as it stays open — and left, if it does not finish,
  // to the next sign-in's sweep, which the missing owner record guarantees.
  try {
    globalThis.localStorage?.removeItem(SDK_CACHE_OWNER_KEY);
  } catch { /* private mode */ }
  if (db) {
    try {
      await terminate(db);
      await Promise.race([
        clearIndexedDbPersistence(db),
        new Promise((resolve) => { setTimeout(resolve, CACHE_BUDGET_MS); }),
      ]);
    } catch (err) {
      console.warn('[auth] the deleted studio\'s documents may still be in the Firestore cache on this device until the next sign-in:', err);
    }
  }
}
